import { makeAutoObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { v4 as uuidv4 } from 'uuid';

import { storage } from '../LocalStorage';

/**
 * Maximum age of a persisted idempotency key before it is treated as orphaned.
 *
 * The key is persisted so a create-order request interrupted mid-flight (e.g.
 * the mobile WebView is frozen or killed while the user is on the payment page)
 * can be safely retried with the same value on the next app launch, letting the
 * backend de-duplicate it. Once this window elapses, any surviving key must not
 * be replayed on a brand-new order.
 */
const STALE_AFTER_MS = 15 * 60 * 1000;

/**
 * Owns the lifecycle of the order-creation `Idempotency-Key`: generation,
 * staleness and reset. Persisted on its own so an in-flight key survives an app
 * restart (retry safety) while a stale key is never reused for a new order.
 */
export class OrderIdempotency {
  /** Current idempotency key, or an empty string when none is active. */
  key = '';

  /** Epoch milliseconds when {@link key} was generated (`0` when unset). */
  createdAt = 0;

  constructor() {
    makeAutoObservable(this);
    makePersistable(this, {
      name: 'OrderIdempotency',
      properties: ['key', 'createdAt'],
      storage,
    }).catch((error) => error && console.error(error));
  }

  /** Whether there is no key or the current one has outlived its retry window. */
  get isStale(): boolean {
    return !this.createdAt || Date.now() - this.createdAt > STALE_AFTER_MS;
  }

  /**
   * Returns the key for the current checkout attempt, generating a fresh one
   * when there is none or the previous one is stale.
   */
  ensure(): string {
    if (!this.key || this.isStale) {
      this.key = uuidv4();
      this.createdAt = Date.now();
    }
    return this.key;
  }

  /** Clears the current key so the next attempt starts from a fresh one. */
  reset(): void {
    this.key = '';
    this.createdAt = 0;
  }
}
